import {
  ApiResponse,
  AuthResponse,
  UserLogin,
  UserCreate,
  User,
  AuditLog,
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  ProcessingActivity,
  ProcessingActivityCreate,
  ProcessingActivityUpdate,
  ProcessingActivityStatusUpdate,
  ProcessingActivityStatistics,
  ProcessingActivityTemplate,
  ProcessingStatus,
  ServiceProvider,
  ServiceProviderCreate,
  ServiceProviderUpdate,
  ServiceProviderRole,
  ServiceProviderStatistics,
  ServiceProviderTemplate,
  AvvContract,
  AvvContractCreate,
  AvvContractUpdate,
  AvvStatus,
  PrivacyNotice,
  PrivacyNoticeCreate,
  PrivacyNoticeUpdate,
  PrivacyNoticeVersion,
  PrivacyNoticeSection,
  PrivacyNoticeSectionUpdate,
  PrivacyNoticeChannel,
  PrivacyNoticeStatus,
  PrivacyNoticeStatistics,
  TomMeasure,
  TomMeasureCreate,
  TomMeasureUpdate,
  TomCategory,
  TomStatus,
  TomStatistics,
  TomTemplate,
  DataSubjectRequest,
  DataSubjectRequestCreate,
  DataSubjectRequestUpdate,
  DataSubjectRequestType,
  DataSubjectRequestStatus,
  DataSubjectRequestStatistics,
  DataBreach,
  DataBreachCreate,
  DataBreachUpdate,
  BreachStatus,
  BreachSeverity,
  DataBreachStatistics,
  BreachTemplate,
  ApprovalRequest,
  ApprovalRequestCreate,
  ApprovalDecision,
  ApprovalStatus,
  ApprovalEntityType,
  ApprovalStatistics,
  OrganizationRole,
  OrganizationPlan,
  OnboardingStatus
} from '@dsgvo-buddy/types';
// AvvDocument is used only as a type reference for upload responses (the backend returns full AvvContract)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

/**
 * API Client Configuration
 */
class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.loadToken();
  }

  /**
   * Load token from localStorage
   */
  private loadToken(): void {
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * Save token to localStorage
   */
  public saveToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  /**
   * Remove token from localStorage
   */
  public removeToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  /**
   * Get authorization headers
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          ...this.getHeaders(),
          ...options?.headers
        }
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Request failed'
        };
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  /**
   * GET request
   */
  public async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  public async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * PUT request
   */
  public async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * DELETE request
   */
  public async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  public async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * POST multipart/form-data request (for file uploads)
   */
  public async upload<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const headers: HeadersInit = {};
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }
      // Do NOT set Content-Type — browser sets it with boundary for multipart
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'Upload fehlgeschlagen' };
      }
      return data;
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Netzwerkfehler' };
    }
  }

  /**
   * Return the base URL (for constructing download URLs)
   */
  public getBaseURL(): string {
    return this.baseURL;
  }

  /**
   * Return auth token (for constructing download URLs)
   */
  public getToken(): string | null {
    return this.token;
  }
}

// Create API client instance
const apiClient = new ApiClient(API_BASE_URL);

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Login user
   */
  async login(credentials: UserLogin): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
    if (response.success && response.data) {
      apiClient.saveToken(response.data.token);
    }
    return response;
  },

  /**
   * Register new user
   */
  async register(userData: UserCreate): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>('/auth/register', userData);
    if (response.success && response.data) {
      apiClient.saveToken(response.data.token);
    }
    return response;
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiClient.get<User>('/auth/me');
  },

  /**
   * Logout user
   */
  logout(): void {
    apiClient.removeToken();
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }
};

/**
 * User with organization role
 */
export interface OrgUser extends User {
  organization_role: OrganizationRole;
}

/**
 * Users API
 */
export const usersApi = {
  async getByOrganization(orgId: number): Promise<ApiResponse<OrgUser[]>> {
    return apiClient.get<OrgUser[]>(`/users?organization_id=${orgId}`);
  },

  async create(data: {
    email: string;
    password: string;
    name: string;
    organization_role: OrganizationRole;
    organization_id: number;
  }): Promise<ApiResponse<OrgUser>> {
    return apiClient.post<OrgUser>('/users', data);
  },

  async update(id: number, data: {
    organization_id: number;
    name?: string;
    email?: string;
    organization_role?: OrganizationRole;
    password?: string;
  }): Promise<ApiResponse<OrgUser>> {
    return apiClient.put<OrgUser>(`/users/${id}`, data);
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/users/${id}?organization_id=${orgId}`);
  }
};

/**
 * Audit Log API
 */
export const auditLogApi = {
  /**
   * Get audit logs
   */
  async getLogs(limit?: number): Promise<ApiResponse<AuditLog[]>> {
    const query = limit ? `?limit=${limit}` : '';
    return apiClient.get<AuditLog[]>(`/audit-log${query}`);
  }
};

/**
 * Health Check API
 */
export const healthApi = {
  /**
   * Check API health
   */
  async check(): Promise<ApiResponse<{ status: string; timestamp: string; uptime: number }>> {
    return apiClient.get('/health');
  }
};

/**
 * Organization API
 */
export const organizationApi = {
  /**
   * Create new organization
   */
  async create(orgData: OrganizationCreate): Promise<ApiResponse<Organization>> {
    return apiClient.post<Organization>('/organizations', orgData);
  },

  /**
   * Get all organizations for current user
   */
  async getAll(): Promise<ApiResponse<Organization[]>> {
    return apiClient.get<Organization[]>('/organizations');
  },

  /**
   * Get organization by ID
   */
  async getById(id: number): Promise<ApiResponse<Organization>> {
    return apiClient.get<Organization>(`/organizations/${id}`);
  },

  /**
   * Update organization
   */
  async update(id: number, updates: OrganizationUpdate): Promise<ApiResponse<Organization>> {
    return apiClient.put<Organization>(`/organizations/${id}`, updates);
  },

  /**
   * Delete organization
   */
  async delete(id: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/organizations/${id}`);
  }
};

/**
 * Processing Activity API
 */
export const processingApi = {
  /**
   * Create new processing activity
   */
  async create(data: ProcessingActivityCreate): Promise<ApiResponse<ProcessingActivity>> {
    return apiClient.post<ProcessingActivity>('/processing', data);
  },

  /**
   * Get all processing activities for organization
   */
  async getByOrganization(
    organizationId: number,
    filters?: {
      status?: ProcessingStatus;
      department?: string;
      search?: string;
    }
  ): Promise<ApiResponse<ProcessingActivity[]>> {
    const params = new URLSearchParams({ organization_id: organizationId.toString() });

    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.department) {
      params.append('department', filters.department);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    return apiClient.get<ProcessingActivity[]>(`/processing?${params.toString()}`);
  },

  /**
   * Get processing activity by ID
   */
  async getById(id: number, organizationId: number): Promise<ApiResponse<ProcessingActivity>> {
    return apiClient.get<ProcessingActivity>(
      `/processing/${id}?organization_id=${organizationId}`
    );
  },

  /**
   * Update processing activity
   */
  async update(
    id: number,
    organizationId: number,
    updates: ProcessingActivityUpdate
  ): Promise<ApiResponse<ProcessingActivity>> {
    return apiClient.put<ProcessingActivity>(`/processing/${id}`, {
      ...updates,
      organization_id: organizationId
    });
  },

  /**
   * Update processing activity status
   */
  async updateStatus(
    id: number,
    organizationId: number,
    statusUpdate: ProcessingActivityStatusUpdate
  ): Promise<ApiResponse<ProcessingActivity>> {
    return apiClient.patch<ProcessingActivity>(`/processing/${id}/status`, {
      ...statusUpdate,
      organization_id: organizationId
    });
  },

  /**
   * Delete processing activity
   */
  async delete(id: number, organizationId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(
      `/processing/${id}?organization_id=${organizationId}`
    );
  },

  /**
   * Get departments for organization
   */
  async getDepartments(organizationId: number): Promise<ApiResponse<string[]>> {
    return apiClient.get<string[]>(
      `/processing/departments?organization_id=${organizationId}`
    );
  },

  /**
   * Get processing activity templates
   */
  async getTemplates(): Promise<ApiResponse<ProcessingActivityTemplate[]>> {
    return apiClient.get<ProcessingActivityTemplate[]>('/processing/templates');
  },

  /**
   * Get statistics for organization
   */
  async getStatistics(organizationId: number): Promise<ApiResponse<ProcessingActivityStatistics>> {
    return apiClient.get<ProcessingActivityStatistics>(
      `/processing/statistics?organization_id=${organizationId}`
    );
  }
};

/**
 * Service Provider API (Dienstleister)
 */
export const serviceProviderApi = {
  async getByOrganization(
    orgId: number,
    filters?: { role?: ServiceProviderRole; search?: string }
  ): Promise<ApiResponse<ServiceProvider[]>> {
    const params = new URLSearchParams({ organization_id: orgId.toString() });
    if (filters?.role) params.append('role', filters.role);
    if (filters?.search) params.append('search', filters.search);
    return apiClient.get<ServiceProvider[]>(`/service-providers?${params.toString()}`);
  },

  async getById(id: number, orgId: number): Promise<ApiResponse<ServiceProvider>> {
    return apiClient.get<ServiceProvider>(`/service-providers/${id}?organization_id=${orgId}`);
  },

  async create(data: ServiceProviderCreate): Promise<ApiResponse<ServiceProvider>> {
    return apiClient.post<ServiceProvider>('/service-providers', data);
  },

  async update(id: number, orgId: number, updates: ServiceProviderUpdate): Promise<ApiResponse<ServiceProvider>> {
    return apiClient.put<ServiceProvider>(`/service-providers/${id}`, { ...updates, organization_id: orgId });
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/service-providers/${id}?organization_id=${orgId}`);
  },

  async getStatistics(orgId: number): Promise<ApiResponse<ServiceProviderStatistics>> {
    return apiClient.get<ServiceProviderStatistics>(`/service-providers/statistics?organization_id=${orgId}`);
  },

  async getExpiring(orgId: number): Promise<ApiResponse<ServiceProvider[]>> {
    return apiClient.get<ServiceProvider[]>(`/service-providers/expiring?organization_id=${orgId}`);
  },

  async linkActivity(providerId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.post<null>(`/service-providers/${providerId}/link/${activityId}`, { organization_id: orgId });
  },

  async unlinkActivity(providerId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/service-providers/${providerId}/link/${activityId}?organization_id=${orgId}`);
  },

  async getLinkedActivities(providerId: number, orgId: number): Promise<ApiResponse<ProcessingActivity[]>> {
    return apiClient.get<ProcessingActivity[]>(`/service-providers/${providerId}/processing-activities?organization_id=${orgId}`);
  },

  async getByProcessingActivity(activityId: number, orgId: number): Promise<ApiResponse<ServiceProvider[]>> {
    return apiClient.get<ServiceProvider[]>(`/service-providers/by-processing/${activityId}?organization_id=${orgId}`);
  },

  async getTemplates(): Promise<ApiResponse<ServiceProviderTemplate[]>> {
    return apiClient.get<ServiceProviderTemplate[]>('/service-providers/templates');
  }
};

/**
 * AVV Contract API (Auftragsverarbeitungsverträge)
 */
export const avvContractApi = {
  async getByProvider(providerId: number, orgId: number): Promise<ApiResponse<AvvContract[]>> {
    return apiClient.get<AvvContract[]>(`/service-providers/${providerId}/avv?organization_id=${orgId}`);
  },

  async create(providerId: number, data: AvvContractCreate, orgId: number): Promise<ApiResponse<AvvContract>> {
    return apiClient.post<AvvContract>(`/service-providers/${providerId}/avv`, { ...data, organization_id: orgId });
  },

  async update(providerId: number, avvId: number, updates: AvvContractUpdate, orgId: number): Promise<ApiResponse<AvvContract>> {
    return apiClient.put<AvvContract>(`/service-providers/${providerId}/avv/${avvId}`, { ...updates, organization_id: orgId });
  },

  async updateStatus(providerId: number, avvId: number, status: AvvStatus, orgId: number): Promise<ApiResponse<AvvContract>> {
    return apiClient.patch<AvvContract>(`/service-providers/${providerId}/avv/${avvId}/status`, { status, organization_id: orgId });
  },

  async delete(providerId: number, avvId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/service-providers/${providerId}/avv/${avvId}?organization_id=${orgId}`);
  },

  async uploadDocuments(providerId: number, avvId: number, orgId: number, files: File[]): Promise<ApiResponse<AvvContract>> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return apiClient.upload<AvvContract>(
      `/service-providers/${providerId}/avv/${avvId}/upload?organization_id=${orgId}`,
      formData
    );
  },

  async deleteDocument(providerId: number, avvId: number, orgId: number, filename: string): Promise<ApiResponse<AvvContract>> {
    return apiClient.delete<AvvContract>(
      `/service-providers/${providerId}/avv/${avvId}/upload/${encodeURIComponent(filename)}?organization_id=${orgId}`
    );
  },

  getDownloadUrl(providerId: number, avvId: number, orgId: number, filename: string): string {
    return `${apiClient.getBaseURL()}/service-providers/${providerId}/avv/${avvId}/upload/${encodeURIComponent(filename)}?organization_id=${orgId}`;
  },

  getToken(): string | null {
    return apiClient.getToken();
  }
};

/**
 * Privacy Notice API (Datenschutzhinweise)
 */
export const privacyNoticeApi = {
  async getByOrganization(orgId: number, filters?: { channel?: PrivacyNoticeChannel; status?: PrivacyNoticeStatus; search?: string }): Promise<ApiResponse<PrivacyNotice[]>> {
    const params = new URLSearchParams({ organization_id: orgId.toString() });
    if (filters?.channel) params.append('channel', filters.channel);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    return apiClient.get<PrivacyNotice[]>(`/privacy-notices?${params.toString()}`);
  },

  async getById(id: number, orgId: number): Promise<ApiResponse<PrivacyNotice>> {
    return apiClient.get<PrivacyNotice>(`/privacy-notices/${id}?organization_id=${orgId}`);
  },

  async create(data: PrivacyNoticeCreate): Promise<ApiResponse<PrivacyNotice>> {
    return apiClient.post<PrivacyNotice>('/privacy-notices', data);
  },

  async update(id: number, orgId: number, updates: PrivacyNoticeUpdate): Promise<ApiResponse<PrivacyNotice>> {
    return apiClient.put<PrivacyNotice>(`/privacy-notices/${id}`, { ...updates, organization_id: orgId });
  },

  async updateStatus(id: number, orgId: number, status: PrivacyNoticeStatus): Promise<ApiResponse<PrivacyNotice>> {
    return apiClient.patch<PrivacyNotice>(`/privacy-notices/${id}/status`, { status, organization_id: orgId });
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/privacy-notices/${id}?organization_id=${orgId}`);
  },

  async getStatistics(orgId: number): Promise<ApiResponse<PrivacyNoticeStatistics>> {
    return apiClient.get<PrivacyNoticeStatistics>(`/privacy-notices/statistics?organization_id=${orgId}`);
  },

  async linkActivity(noticeId: number, paId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.post<null>(`/privacy-notices/${noticeId}/link/${paId}`, { organization_id: orgId });
  },

  async unlinkActivity(noticeId: number, paId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/privacy-notices/${noticeId}/link/${paId}?organization_id=${orgId}`);
  },

  async getVersions(noticeId: number, orgId: number): Promise<ApiResponse<PrivacyNoticeVersion[]>> {
    return apiClient.get<PrivacyNoticeVersion[]>(`/privacy-notices/${noticeId}/versions?organization_id=${orgId}`);
  },

  async createVersion(noticeId: number, orgId: number, notes?: string): Promise<ApiResponse<PrivacyNoticeVersion>> {
    return apiClient.post<PrivacyNoticeVersion>(`/privacy-notices/${noticeId}/versions`, { organization_id: orgId, notes });
  },

  async updateSection(noticeId: number, sectionId: number, orgId: number, updates: PrivacyNoticeSectionUpdate): Promise<ApiResponse<PrivacyNoticeSection>> {
    return apiClient.put<PrivacyNoticeSection>(`/privacy-notices/${noticeId}/sections/${sectionId}`, { ...updates, organization_id: orgId });
  },

  async regenerate(noticeId: number, orgId: number): Promise<ApiResponse<PrivacyNotice>> {
    return apiClient.post<PrivacyNotice>(`/privacy-notices/${noticeId}/regenerate`, { organization_id: orgId });
  },

  async exportNotice(noticeId: number, orgId: number, format: 'json' | 'text' | 'html'): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/privacy-notices/${noticeId}/export?organization_id=${orgId}&format=${format}`);
  }
};

/**
 * TOM API (Technische und Organisatorische Maßnahmen)
 */
export const tomApi = {
  async getByOrganization(
    orgId: number,
    filters?: { category?: TomCategory; status?: TomStatus; search?: string }
  ): Promise<ApiResponse<TomMeasure[]>> {
    const params = new URLSearchParams({ organization_id: orgId.toString() });
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    return apiClient.get<TomMeasure[]>(`/tom?${params.toString()}`);
  },

  async getById(id: number, orgId: number): Promise<ApiResponse<TomMeasure>> {
    return apiClient.get<TomMeasure>(`/tom/${id}?organization_id=${orgId}`);
  },

  async create(data: TomMeasureCreate): Promise<ApiResponse<TomMeasure>> {
    return apiClient.post<TomMeasure>('/tom', data);
  },

  async update(id: number, orgId: number, updates: TomMeasureUpdate): Promise<ApiResponse<TomMeasure>> {
    return apiClient.put<TomMeasure>(`/tom/${id}`, { ...updates, organization_id: orgId });
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/tom/${id}?organization_id=${orgId}`);
  },

  async getStatistics(orgId: number): Promise<ApiResponse<TomStatistics>> {
    return apiClient.get<TomStatistics>(`/tom/statistics?organization_id=${orgId}`);
  },

  async getTemplates(): Promise<ApiResponse<TomTemplate[]>> {
    return apiClient.get<TomTemplate[]>('/tom/templates');
  },

  async getCategories(orgId: number): Promise<ApiResponse<string[]>> {
    return apiClient.get<string[]>(`/tom/categories?organization_id=${orgId}`);
  },

  async linkActivity(tomId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.post<null>(`/tom/${tomId}/link/${activityId}`, { organization_id: orgId });
  },

  async unlinkActivity(tomId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/tom/${tomId}/link/${activityId}?organization_id=${orgId}`);
  },

  async getLinkedActivities(tomId: number, orgId: number): Promise<ApiResponse<ProcessingActivity[]>> {
    return apiClient.get<ProcessingActivity[]>(`/tom/${tomId}/processing-activities?organization_id=${orgId}`);
  },

  async getByProcessingActivity(activityId: number, orgId: number): Promise<ApiResponse<TomMeasure[]>> {
    return apiClient.get<TomMeasure[]>(`/tom/by-processing/${activityId}?organization_id=${orgId}`);
  }
};

/**
 * Data Subject Request API (Betroffenenrechte)
 */
export const dsrApi = {
  async getByOrganization(
    orgId: number,
    filters?: { request_type?: DataSubjectRequestType; status?: DataSubjectRequestStatus; search?: string }
  ): Promise<ApiResponse<DataSubjectRequest[]>> {
    const params = new URLSearchParams({ organization_id: orgId.toString() });
    if (filters?.request_type) params.append('request_type', filters.request_type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    return apiClient.get<DataSubjectRequest[]>(`/data-subject-requests?${params.toString()}`);
  },

  async getById(id: number, orgId: number): Promise<ApiResponse<DataSubjectRequest>> {
    return apiClient.get<DataSubjectRequest>(`/data-subject-requests/${id}?organization_id=${orgId}`);
  },

  async create(data: DataSubjectRequestCreate): Promise<ApiResponse<DataSubjectRequest>> {
    return apiClient.post<DataSubjectRequest>('/data-subject-requests', data);
  },

  async update(id: number, orgId: number, updates: DataSubjectRequestUpdate): Promise<ApiResponse<DataSubjectRequest>> {
    return apiClient.put<DataSubjectRequest>(`/data-subject-requests/${id}`, { ...updates, organization_id: orgId });
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/data-subject-requests/${id}?organization_id=${orgId}`);
  },

  async getStatistics(orgId: number): Promise<ApiResponse<DataSubjectRequestStatistics>> {
    return apiClient.get<DataSubjectRequestStatistics>(`/data-subject-requests/statistics?organization_id=${orgId}`);
  },

  async linkActivity(dsrId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.post<null>(`/data-subject-requests/${dsrId}/link/${activityId}`, { organization_id: orgId });
  },

  async unlinkActivity(dsrId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/data-subject-requests/${dsrId}/link/${activityId}?organization_id=${orgId}`);
  },

  async getLinkedActivities(dsrId: number, orgId: number): Promise<ApiResponse<ProcessingActivity[]>> {
    return apiClient.get<ProcessingActivity[]>(`/data-subject-requests/${dsrId}/processing-activities?organization_id=${orgId}`);
  }
};

/**
 * Data Breach API (Datenschutzvorfälle)
 */
export const breachApi = {
  async getByOrganization(
    orgId: number,
    filters?: { severity?: BreachSeverity; status?: BreachStatus; search?: string }
  ): Promise<ApiResponse<DataBreach[]>> {
    const params = new URLSearchParams({ organization_id: orgId.toString() });
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    return apiClient.get<DataBreach[]>(`/breaches?${params.toString()}`);
  },

  async getById(id: number, orgId: number): Promise<ApiResponse<DataBreach>> {
    return apiClient.get<DataBreach>(`/breaches/${id}?organization_id=${orgId}`);
  },

  async create(data: DataBreachCreate): Promise<ApiResponse<DataBreach>> {
    return apiClient.post<DataBreach>('/breaches', data);
  },

  async update(id: number, orgId: number, updates: DataBreachUpdate): Promise<ApiResponse<DataBreach>> {
    return apiClient.put<DataBreach>(`/breaches/${id}`, { ...updates, organization_id: orgId });
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/breaches/${id}?organization_id=${orgId}`);
  },

  async getStatistics(orgId: number): Promise<ApiResponse<DataBreachStatistics>> {
    return apiClient.get<DataBreachStatistics>(`/breaches/statistics?organization_id=${orgId}`);
  },

  async getTemplates(): Promise<ApiResponse<BreachTemplate[]>> {
    return apiClient.get<BreachTemplate[]>('/breaches/templates');
  },

  async linkProcessing(breachId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.post<null>(`/breaches/${breachId}/link-processing/${activityId}`, { organization_id: orgId });
  },

  async unlinkProcessing(breachId: number, activityId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/breaches/${breachId}/link-processing/${activityId}?organization_id=${orgId}`);
  },

  async getLinkedActivities(breachId: number, orgId: number): Promise<ApiResponse<ProcessingActivity[]>> {
    return apiClient.get<ProcessingActivity[]>(`/breaches/${breachId}/processing-activities?organization_id=${orgId}`);
  },

  async linkProvider(breachId: number, providerId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.post<null>(`/breaches/${breachId}/link-provider/${providerId}`, { organization_id: orgId });
  },

  async unlinkProvider(breachId: number, providerId: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/breaches/${breachId}/link-provider/${providerId}?organization_id=${orgId}`);
  },

  async getLinkedProviders(breachId: number, orgId: number): Promise<ApiResponse<ServiceProvider[]>> {
    return apiClient.get<ServiceProvider[]>(`/breaches/${breachId}/service-providers?organization_id=${orgId}`);
  }
};

/**
 * Approval API (Freigabeworkflows)
 */
export const approvalApi = {
  async getByOrganization(
    orgId: number,
    filters?: { status?: ApprovalStatus; entity_type?: ApprovalEntityType }
  ): Promise<ApiResponse<ApprovalRequest[]>> {
    const params = new URLSearchParams({ organization_id: orgId.toString() });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.entity_type) params.append('entity_type', filters.entity_type);
    return apiClient.get<ApprovalRequest[]>(`/approvals?${params.toString()}`);
  },

  async getById(id: number, orgId: number): Promise<ApiResponse<ApprovalRequest>> {
    return apiClient.get<ApprovalRequest>(`/approvals/${id}?organization_id=${orgId}`);
  },

  async create(data: ApprovalRequestCreate): Promise<ApiResponse<ApprovalRequest>> {
    return apiClient.post<ApprovalRequest>('/approvals', data);
  },

  async decide(id: number, orgId: number, decision: ApprovalDecision): Promise<ApiResponse<ApprovalRequest>> {
    return apiClient.post<ApprovalRequest>(`/approvals/${id}/decide`, { ...decision, organization_id: orgId });
  },

  async withdraw(id: number, orgId: number): Promise<ApiResponse<ApprovalRequest>> {
    return apiClient.post<ApprovalRequest>(`/approvals/${id}/withdraw`, { organization_id: orgId });
  },

  async delete(id: number, orgId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/approvals/${id}?organization_id=${orgId}`);
  },

  async getStatistics(orgId: number): Promise<ApiResponse<ApprovalStatistics>> {
    return apiClient.get<ApprovalStatistics>(`/approvals/statistics?organization_id=${orgId}`);
  },

  async getByEntity(entityType: ApprovalEntityType, entityId: number, orgId: number): Promise<ApiResponse<ApprovalRequest[]>> {
    return apiClient.get<ApprovalRequest[]>(`/approvals/entity/${entityType}/${entityId}?organization_id=${orgId}`);
  },

  async getPendingCount(orgId: number): Promise<number> {
    const response = await apiClient.get<ApprovalStatistics>(`/approvals/statistics?organization_id=${orgId}`);
    if (response.success && response.data) {
      return response.data.pending;
    }
    return 0;
  }
};

/**
 * Export API (JSON-Export)
 */
/**
 * Reports API
 */
export const reportsApi = {
  getReportUrl(report: string, orgId: number, format: 'html' | 'json' = 'html'): string {
    return `${API_BASE_URL}/reports/${report}?organization_id=${orgId}&format=${format}`;
  },
  async getCompliance(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/reports/compliance?organization_id=${orgId}`);
  }
};

export const exportApi = {
  async exportOrganization(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/export/organizations?organization_id=${orgId}`);
  },
  async exportProcessing(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/export/processing?organization_id=${orgId}`);
  },
  async exportServiceProviders(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/export/service-providers?organization_id=${orgId}`);
  },
  async exportTom(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/export/tom?organization_id=${orgId}`);
  },
  async exportPrivacyNotices(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/export/privacy-notices?organization_id=${orgId}`);
  },
  async exportFull(orgId: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`/export/full?organization_id=${orgId}`);
  }
};

/**
 * Import API (JSON-Import)
 */
export const importApi = {
  async dryRun(orgId: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>('/import/dry-run', { organization_id: orgId, data });
  },
  async execute(orgId: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>('/import/execute', { organization_id: orgId, data });
  }
};

/**
 * Billing API
 */
export interface PromoInfo {
  active: boolean;
  regular_price: number;
  promo_price: number;
  valid_until: string | null;
}

export interface BillingPlanInfo {
  plan: OrganizationPlan;
  organization_id: number;
  organization_name: string;
  user_count: number;
  stripe_configured: boolean;
  promo: PromoInfo | null;
}

export const billingApi = {
  async getPlan(): Promise<ApiResponse<BillingPlanInfo>> {
    return apiClient.get<BillingPlanInfo>('/billing/plan');
  },

  async createCheckout(): Promise<ApiResponse<{ checkout_url: string }>> {
    return apiClient.post<{ checkout_url: string }>('/billing/create-checkout', {});
  },

  async createPortalSession(): Promise<ApiResponse<{ portal_url: string }>> {
    return apiClient.post<{ portal_url: string }>('/billing/portal', {});
  },
};

/**
 * Onboarding API (Datenschutz-Kompass)
 */
export const onboardingApi = {
  async getStatus(): Promise<ApiResponse<OnboardingStatus>> {
    return apiClient.get<OnboardingStatus>('/onboarding');
  },
  async confirmStep(stepKey: string): Promise<ApiResponse<OnboardingStatus>> {
    return apiClient.post<OnboardingStatus>(`/onboarding/${stepKey}/confirm`, {});
  },
  async dismissStep(stepKey: string): Promise<ApiResponse<OnboardingStatus>> {
    return apiClient.post<OnboardingStatus>(`/onboarding/${stepKey}/dismiss`, {});
  },
  async resetStep(stepKey: string): Promise<ApiResponse<OnboardingStatus>> {
    return apiClient.post<OnboardingStatus>(`/onboarding/${stepKey}/reset`, {});
  },
};

/**
 * Admin API (Conversion Export)
 */
export const adminApi = {
  async getConversionStats(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/admin/conversions/stats');
  },
  async triggerExport(): Promise<ApiResponse<any>> {
    return apiClient.post<any>('/admin/conversions/export', {});
  },
  getDownloadUrl(filename: string): string {
    return `${API_BASE_URL}/admin/conversions/download/${encodeURIComponent(filename)}`;
  },
};

/**
 * Helper: trigger browser download of JSON data
 */
export function downloadJson(data: any, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open an HTML report in a new tab (for PDF printing via Ctrl+P)
 */
export async function openPdfExport(reportPath: string, orgId: number): Promise<void> {
  const url = `${API_BASE_URL}/reports/${reportPath}?organization_id=${orgId}&format=html`;
  const res = await fetch(url, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
  });
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

export default apiClient;
